// Type only: MiraDataQualityView imports this module, so a value import would make a runtime cycle.
import type { MiraViewContext } from "@/utils/MiraDataQualityView";

/**
 * What a Mira figure's case count (`basis`) counts, so every place that shows it names the right unit
 * (Hans, Track #2610072 S1: "show always the probe numbers"). The tile captions already name their unit in
 * their own text; this map serves the places that show a count without a caption of their own: the Verlauf
 * tooltip, the Rangliste rows and the comparison bars.
 *
 * Read from what each figure's `basis` counts in IRES Cloud, as the tile captions state it. IRES Cloud's
 * metric registry has a coarser `population` per metric (energy per project is listed under its
 * assignments, for one), so it is not the source.
 */
export type MiraCaseUnit =
  | "projects"
  | "dryings"
  | "assignments"
  | "assignmentsWithReading"
  | "readings"
  | "offers"
  | "answers"
  | "deviceDays"
  | "devices";

/**
 * The fewest cases a row inside a card needs to show its average or share (Hans, Track #2610072 S1,
 * 2026-10-23): the same 10 as a tile, so a row on one or two dryings no longer reads like a finding. A row
 * below it keeps its label and its case count and says "zu wenige" instead of a value. Counts (Projekte je
 * Versicherung, Schadenursachen, ...) have no floor, as a count tile has none.
 */
export const MIRA_ROW_CASE_FLOOR = 10;

/** Whether a row's average or share rests on too few cases to show. */
export function belowRowFloor(cases: number): boolean {
  return cases < MIRA_ROW_CASE_FLOOR;
}

/** Every figure not listed counts finished projects. */
const DEFAULT_UNIT: MiraCaseUnit = "projects";

const UNIT_BY_METRIC: Record<string, MiraCaseUnit> = {
  // Dryings: a HyDry drying chain, or a drying with an accepted offer.
  "MIRA-DRY-001": "dryings",
  "MIRA-DRY-002": "dryings",
  "MIRA-DRY-005": "dryings",
  "MIRA-DRY-008": "dryings",
  "MIRA-INS-006": "dryings",
  // Device assignments, with or without a meter reading, and the readings themselves.
  "MIRA-DEV-001": "assignments",
  "MIRA-DQ-002": "assignments",
  "MIRA-DEV-004": "assignmentsWithReading",
  "MIRA-DEV-002": "readings",
  // Insurer offers and their answers.
  "MIRA-INS-004": "offers",
  "MIRA-INS-005": "answers",
  // Device days: Flottenauslastung's headline fleet (devices x days owned) and the category mix.
  "MIRA-DEV-006": "deviceDays",
  "MIRA-DEV-008": "deviceDays",
  // Devices: Flottenauslastung per fleet names its devices (Hans, #2610072 S1 / S7).
  "MIRA-DEV-011": "devices",
  "MIRA-DEV-012": "devices",
  "MIRA-DEV-013": "devices",
  "MIRA-DEV-014": "devices",
};

/** The unit a figure's case count is counted in. */
export function caseUnit(metricId: string): MiraCaseUnit {
  return UNIT_BY_METRIC[metricId] ?? DEFAULT_UNIT;
}

/**
 * Whether a figure's case count is the value itself (Projekte: the number of finished projects), so
 * showing it beside the value would only repeat it.
 */
export function caseCountIsValue(metricId: string): boolean {
  return metricId === "MIRA-PRJ-001";
}

/** A count in a given unit, for a part counted differently from its figure (INS-006's offers with a request). */
export function unitText(unit: MiraCaseUnit, count: number, ctx: MiraViewContext): string {
  return count === 1 ? ctx.t(`mira.units.${unit}One`) : ctx.t(`mira.units.${unit}`, [ctx.formatNumber(count)]);
}

/** "412 Projekte", "1 Trocknung": a case count in the figure's own unit, formatted for the locale. */
export function casesText(metricId: string, count: number, ctx: MiraViewContext): string {
  return unitText(caseUnit(metricId), count, ctx);
}
